import React, { memo } from 'react';
import { AppNode, NodeType, PanoramaQuality } from '@/types';
import { ConfirmPanel } from './ConfirmPanel';
import { ArrowLeft, Camera, Grid3x3, LayoutGrid, RotateCcw, Maximize2, Crosshair } from 'lucide-react';

interface PanoramaPanelProps {
  data: AppNode;
  cardHeight: number;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onGeneratePanorama?: (sourceNodeId: string, params: {
    prompt: string;
    quality: import('@/types').PanoramaQuality;
    referenceImageUrl?: string;
    mode: 'txt2panorama' | 'img2panorama';
    model?: string;
  }) => void;
  onClose: () => void;
}

export const PanoramaPanel: React.FC<PanoramaPanelProps> = ({
  data,
  cardHeight,
  onNodeUpdate,
  onGeneratePanorama,
  onClose,
}) => {
  const handleConfirm = () => {
    const autoPrompt = '生成一张720°全景图（equirectangular 投影，2:1 比例），画面中不得出现任何人物或动物。';
    const referenceImage = data.data.image;
    const selectedModel = data.data.model || 'gemini-2.5-flash-image';
    const selectedResolution = data.data.resolution || '1k';
    const qualityMap: Record<string, PanoramaQuality> = { '1k': PanoramaQuality.STANDARD, '2k': PanoramaQuality.HD, '4k': PanoramaQuality.ULTRA };
    const selectedQuality = qualityMap[selectedResolution] || PanoramaQuality.HD;

    onClose();
    onGeneratePanorama?.(data.id, {
      prompt: autoPrompt,
      quality: selectedQuality,
      referenceImageUrl: referenceImage,
      mode: referenceImage ? 'img2panorama' : 'txt2panorama',
      model: selectedModel,
    });
  };

  return (
    <ConfirmPanel
      data={data}
      cardHeight={cardHeight}
      title="720°全景生成"
      modelFilter="img2img"
      onNodeUpdate={onNodeUpdate}
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  );
};

interface PanoramaToolbarProps {
  data: AppNode;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
}

export const PanoramaToolbar: React.FC<PanoramaToolbarProps> = memo(({ data, onNodeUpdate }) => {
  return (
    <div className="flex items-center gap-1 nodrag bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg">
      <button
        onClick={(e) => { e.stopPropagation(); onNodeUpdate(data.id, { isPanoramaPreviewOpen: false }); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="退出全景"
      >
        <ArrowLeft size={16} />
      </button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <button
        onClick={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="当前视角截图"
      >
        <Camera size={16} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="4视角截图"
      >
        <Grid3x3 size={16} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="12视角截图"
      >
        <LayoutGrid size={16} />
      </button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <button
        onClick={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="构图参考线"
      >
        <Crosshair size={16} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="重置视角"
      >
        <RotateCcw size={16} />
      </button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <button
        onClick={(e) => { e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        title="全屏"
      >
        <Maximize2 size={16} />
      </button>
    </div>
  );
});

PanoramaToolbar.displayName = 'PanoramaToolbar';
